import React, { useState } from 'react';
import { Key, Loader2, CheckCircle2, Calendar, Shield, AlertCircle } from 'lucide-react';

export interface LicenseOption {
  licenseId: string;
  productType: string;
  expiryDate: string;
  status: string;
  platform?: string;
}

interface LicenseSelectionModalProps {
  licenses: LicenseOption[];
  onSelect: (licenseId: string) => void;
  onCancel: () => void;
  loading?: boolean;
  currentPlatform?: string;
  error?: string;
}

export const LicenseSelectionModal: React.FC<LicenseSelectionModalProps> = ({
  licenses,
  onSelect,
  onCancel,
  loading = false,
  currentPlatform = '',
  error = ''
}) => {
  const [selectedLicense, setSelectedLicense] = useState<string | null>(null);

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getDaysRemaining = (dateStr: string) => {
    try {
      const expiry = new Date(dateStr);
      const now = new Date();
      const diff = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return diff;
    } catch {
      return 0;
    }
  };

  const getTierColor = (productType: string) => {
    const type = productType.toLowerCase();
    if (type.includes('professional') || type.includes('pro')) return 'blue';
    if (type.includes('basic')) return 'green';
    if (type.includes('demo') || type.includes('trial')) return 'amber';
    return 'slate';
  };

  const normalizePlatform = (p: string): string => {
    const s = p.toLowerCase();
    if (s === 'darwin' || s === 'mac' || s === 'macos') return 'mac';
    if (s === 'win32' || s === 'windows') return 'windows';
    if (s === 'android') return 'android';
    if (s === 'ios') return 'ios';
    return s;
  };

  const isPlatformCompatible = (licensePlatform: string | undefined): boolean => {
    if (!licensePlatform || licensePlatform === 'All') return true;
    if (!currentPlatform) return true;
    return normalizePlatform(licensePlatform) === normalizePlatform(currentPlatform);
  };

  const getPlatformLabel = (licensePlatform: string | undefined): string | null => {
    if (!licensePlatform || licensePlatform === 'All') return null;
    return licensePlatform;
  };

  const getCurrentPlatformName = (): string => {
    switch (normalizePlatform(currentPlatform)) {
      case 'android': return 'Android';
      case 'ios': return 'iOS';
      case 'windows': return 'Windows';
      case 'mac': return 'Mac';
      default: return currentPlatform;
    }
  };

  const handleConfirm = () => {
    if (selectedLicense) {
      onSelect(selectedLicense);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center">
              <Key className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Select License</h2>
              <p className="text-sm text-slate-400">You have multiple licenses. Choose one to use:</p>
            </div>
          </div>
        </div>

        {/* License List */}
        <div className="p-4 max-h-[400px] overflow-y-auto space-y-3">
          {licenses.map((license) => {
            const daysRemaining = getDaysRemaining(license.expiryDate);
            const color = getTierColor(license.productType);
            const isExpired = daysRemaining < 0;
            const compatible = isPlatformCompatible(license.platform);
            const isDisabled = isExpired || !compatible;
            const isSelected = selectedLicense === license.licenseId;
            const platformLabel = getPlatformLabel(license.platform);

            return (
              <button
                key={license.licenseId}
                onClick={() => !isDisabled && setSelectedLicense(license.licenseId)}
                disabled={isDisabled}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-500/10'
                    : isDisabled
                    ? 'border-slate-700 bg-slate-800/50 opacity-50 cursor-not-allowed'
                    : 'border-slate-700 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase bg-${color}-500/20 text-${color}-400`}>
                        {license.productType}
                      </span>
                      {platformLabel && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-slate-600/40 text-slate-300">
                          {platformLabel}
                        </span>
                      )}
                      {isExpired && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-red-500/20 text-red-400">
                          Expired
                        </span>
                      )}
                      {!isExpired && !compatible && (
                        <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-orange-500/20 text-orange-400">
                          Wrong device
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-mono">{license.licenseId}</p>
                    {!isExpired && !compatible && (
                      <p className="text-xs text-orange-400 mt-1">
                        This license is for {platformLabel} — not compatible with this {getCurrentPlatformName()} device
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <Calendar className="w-3 h-3" />
                      <span>
                        {isExpired ? 'Expired' : `Expires ${formatDate(license.expiryDate)}`}
                        {!isExpired && daysRemaining <= 30 && (
                          <span className={`ml-1 ${daysRemaining <= 7 ? 'text-red-400' : 'text-amber-400'}`}>
                            ({daysRemaining} days left)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-slate-600'
                  }`}>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 pb-2">
            <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || !selectedLicense}
            className="flex-1 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Use Selected License
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
